import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);
const LOGOUT_TIMEOUT_MS = 3000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutRequest = useRef(null);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      api.get('/auth/me')
        .then(response => {
          if (logoutRequest.current) return;
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, full_name, role = 'admin') => {
    const response = await api.post('/auth/register', {
      email,
      password,
      full_name,
      role
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  // Presence heartbeat — keeps user marked as "Available" in Teams Chat
  useEffect(() => {
    let heartbeatInterval = null;
    if (user) {
      // Send initial heartbeat immediately
      api.post('/teams/heartbeat').catch(() => {});
      // Then every 60 seconds
      heartbeatInterval = setInterval(() => {
        api.post('/teams/heartbeat').catch(() => {});
      }, 60000);
    }
    return () => {
      if (heartbeatInterval) clearInterval(heartbeatInterval);
    };
  }, [user]);

  const logout = () => {
    if (logoutRequest.current) return logoutRequest.current;
    logoutRequest.current = (async () => {
      let timeoutId;
      try {
        // Keep the token available while recording the explicit sign-out and
        // presence change. The logout endpoint audits; it does not revoke JWTs.
        await Promise.race([
          Promise.allSettled([
            api.post('/auth/logout', undefined, { timeout: LOGOUT_TIMEOUT_MS }),
            api.post('/teams/go-offline', undefined, { timeout: LOGOUT_TIMEOUT_MS }),
          ]),
          new Promise((resolve) => { timeoutId = setTimeout(resolve, LOGOUT_TIMEOUT_MS); }),
        ]);
      } catch {
        // Local sign-out must still finish if a request cannot be started.
      } finally {
        clearTimeout(timeoutId);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        window.location.href = '/login';
      }
    })();
    return logoutRequest.current;
  };

  // Handle SSO login - set user from token already in localStorage
  const loginWithSSO = async () => {
    const response = await api.get('/auth/me');
    const userData = response.data;
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  // Re-fetch the current user (used after a self-service profile update)
  const refreshUser = async () => {
    const response = await api.get('/auth/me');
    const userData = response.data;
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, loginWithSSO, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
